import {
  useMutation,
  useQuery,
  useQueryClient,
  type UseMutationResult,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { ApiError } from '@app/api/ApiError';
import { shellKeys } from '@app/api/hooks/shell';
import { del, get, post, put, withPath, type ApiPath } from '@app/api/typed';
import type {
  SiteConnectionTest,
  SiteCreate,
  SiteList,
  SiteUpdate,
} from './SiteModel';

/**
 * Hooks da tela «Sites» (P1).
 *
 * Toda mutação invalida a lista: quem manda no que a tela mostra é o que ficou
 * gravado, não o que foi enviado. O «Testar conexão» invalida TAMBÉM — ele
 * persiste `site.last_probe_*`, e é dele que sai a coluna «saúde».
 */
export const sitesKeys = {
  all: ['sites'] as const,
  list: () => ['sites', 'list'] as const,
};

/** URL com `{id}` preenchido sem perder o tipo do path dos helpers tipados. */
function route<P extends ApiPath>(template: P, id: string): P {
  return withPath(template, { id }) as P;
}

export function useSites(): UseQueryResult<SiteList, ApiError> {
  return useQuery<SiteList, ApiError>({
    queryKey: sitesKeys.list(),
    queryFn: () => get('/api/v1/sites'),
  });
}

/**
 * «Este tenant tem mais de um site» — o interruptor da REVELAÇÃO PROGRESSIVA.
 *
 * Princípio nº 1 do multi-site: **com um site só, nada muda em tela nenhuma.** As
 * telas que herdam a coluna de site (Identidades, Recursos) a escondem quando isto
 * é falso, e é assim que o produto não fica mais complicado para quem tem um
 * cluster — que é todo cliente de hoje.
 *
 * O valor vem do SERVIDOR (`SiteList.multi`), nunca de `items.length > 1` contado
 * aqui: cada tela contando por conta própria divergiria no dia em que a regra
 * mudasse — «mais de um site» pode passar a significar «mais de um site ATIVO», e
 * aí haveria três respostas diferentes na mesma tela.
 *
 * Enquanto a primeira leitura não chega, responde `false`: a coluna aparecer
 * depois é degradação; ela piscar e sumir é defeito visível.
 */
export function useMultiSite(): boolean {
  return useSites().data?.multi ?? false;
}

/**
 * Os slugs dos sites do tenant, para o combobox de filtro — **vazio quando o
 * tenant tem um site só**.
 *
 * Devolver a lista vazia, e não os slugs com `multi` ao lado, é o que faz a
 * revelação progressiva acontecer num lugar só: quem consome desenha o filtro se
 * houver o que escolher, e não precisa lembrar de checar `multi` também. Com um
 * cluster, um filtro de uma opção é pior que filtro nenhum — ele promete um
 * recorte que não recorta nada.
 */
export function useSiteSlugs(): string[] {
  const list = useSites().data;
  if (!list?.multi) return [];
  return list.items.map((site) => site.slug);
}

/**
 * Os sites do tenant como opções de combobox (id + slug) — **vazio em single-site**.
 *
 * Irmão de `useSiteSlugs`, e separado dele por uma razão de contrato: o FILTRO
 * recorta por SLUG (é o que a URL carrega e o que o operador lê), mas o CADASTRO
 * grava por ID (é a chave estrangeira). Um helper só devolvendo strings obrigaria
 * quem cadastra a cruzar slug→id de novo — e a cruzar errado no dia em que dois
 * tenants tiverem slugs iguais.
 */
export function useSiteOptions(): { id: string; slug: string }[] {
  const list = useSites().data;
  if (!list?.multi) return [];
  return list.items.map((site) => ({ id: site.id, slug: site.slug }));
}

/**
 * O trust domain de um site — o do site DA IDENTIDADE, ou o do `default` quando
 * ninguém escolheu (S-b (d)).
 *
 * Existe porque a prévia do SPIFFE ID que T5 desenha enquanto a pessoa digita saía
 * do TENANT, e o tenant deixou de ter trust domain: a prévia teria de sumir ou
 * mentir. Ler da lista é honesto — é a mesma fonte que o servidor usa para compor o
 * `spiffe_id_esperado` que a aba SPIFFE mostra depois de salvar.
 *
 * Devolve `null` enquanto a lista não chegou: quem chama já trata «não dá para
 * prever» (a prévia some), e um valor de fachada aqui inventaria um ID.
 */
export function useSiteTrustDomain(siteId?: string | null): string | null {
  const list = useSites().data;
  if (!list) return null;
  const chosen = siteId ? list.items.find((site) => site.id === siteId) : undefined;
  const site = chosen ?? list.items.find((item) => item.is_default) ?? list.items[0];
  return site?.trust_domain || null;
}

export function useCreateSite(): UseMutationResult<unknown, ApiError, SiteCreate> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, SiteCreate>({
    mutationFn: (body) => post('/api/v1/sites', { body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sitesKeys.all }),
  });
}

export interface UpdateSiteVars {
  id: string;
  body: SiteUpdate;
}

export function useUpdateSite(): UseMutationResult<unknown, ApiError, UpdateSiteVars> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, UpdateSiteVars>({
    mutationFn: ({ id, body }) => put(route('/api/v1/sites/{id}', id), { body }),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sitesKeys.all }),
  });
}

/**
 * Excluir. O banco recusa por RESTRICT quando o site carrega identidade ou
 * recurso, e o `default` recusa com `409 site_padrao` — a tela só esconde o
 * botão, nunca substitui essas duas guardas.
 */
export function useDeleteSite(): UseMutationResult<unknown, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<unknown, ApiError, string>({
    mutationFn: (id) => del(route('/api/v1/sites/{id}', id)),
    onSuccess: () => void queryClient.invalidateQueries({ queryKey: sitesKeys.all }),
  });
}

/**
 * «Testar conexão» — a *verificação executável* do P8.
 *
 * Invalida a lista de propósito: a sondagem é PERSISTIDA (`site.last_probe_*`) e
 * é ela que alimenta a coluna «saúde». Sem a invalidação, o operador veria o
 * resultado no diálogo e a coluna continuaria dizendo «nunca testado».
 *
 * A lista **não** sonda sozinha: com N sites, montá-la dispararia N chamadas a
 * clusters distintos e a página passaria a depender de todos estarem de pé.
 *
 * # E INVALIDA O SHELL, QUE É O CHIP DA TOPBAR
 *
 * Achado ao migrar o e2e do card «Middleware» (S-a): o card invalidava
 * `shellKeys.shell` ao testar, e esta tela não — então a sondagem falhava, o
 * diálogo dizia «não respondeu», e **o chip continuava verde** até algo mais
 * refazer a leitura.
 *
 * A ligação nasceu no mesmo marco: o desfecho de transporte passou a carimbar a
 * linha do SITE, e é dela que a topbar lê. Antes, o chip era do tenant e nada
 * daqui o tocava; agora esta mutação move o dado que ele desenha, e quem move o
 * dado invalida quem o lê.
 */
export function useTestSiteConnection(): UseMutationResult<SiteConnectionTest, ApiError, string> {
  const queryClient = useQueryClient();
  return useMutation<SiteConnectionTest, ApiError, string>({
    mutationFn: (id) => post(route('/api/v1/sites/{id}/test', id), { body: {} }),
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: sitesKeys.all });
      void queryClient.invalidateQueries({ queryKey: shellKeys.shell });
    },
  });
}
